// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Styled, Space} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  useEffect,
  useQuery,
  useResponsive,
  useUrlFilters,
  usePagination,
  PaginationType,
  ResponsiveType,
  UrlFiltersType,
  useState,
} from '@supermove/hooks';
import {ProjectModel, Stage, StageModel, UserModel} from '@supermove/models';
import {colors} from '@supermove/styles';
import {List} from '@supermove/utils';

// App
import Badge from '@shared/design/components/Badge';
import MultiPaneLayout from '@shared/design/components/Layout/MultiPaneLayout';
import {PaneType} from '@shared/design/components/Layout/components/Pane';
import Line from '@shared/design/components/Line';
import PaginationBar from '@shared/design/components/Pagination/PaginationBar';
import Tabs from '@shared/design/components/Tabs';
import {ProjectDetailsFiltersType} from '@shared/modules/Project/components/widgets/ProjectDetailsWidget';
import {ProjectEventLogFiltersType} from '@shared/modules/Project/components/widgets/ProjectEventLogWidget';
import ProjectPaneContent from '@shared/modules/Project/components/widgets/components/ProjectPaneContent';
import ProjectPaneHeader from '@shared/modules/Project/components/widgets/components/ProjectPaneHeader';
import ProjectStatus from '@shared/modules/Project/enums/ProjectStatus';
import ProjectWidgetKind, {
  ProjectWidgetKindType,
} from '@shared/modules/Project/enums/ProjectWidgetKind';
import LoadingStageTabs from '@shared/modules/Sales/components/LoadingStageTabs';
import SalesLeadsFilters, {
  SALES_LEADS_FILTERS_KEYS,
  SalesLeadsFiltersType,
} from '@shared/modules/Sales/components/SalesLeadsFilters';
import SalesLeadsTable from '@shared/modules/Sales/components/SalesLeadsTable';

const PageContentContainer = Styled.View`
  flex: 1;
`;

const SalesPageContentContentContainer = Styled.View<{responsive: ResponsiveType}>`
  flex: 1;
  background-color: ${({responsive}) =>
    responsive.mobile ? colors.white : colors.gray.background};
`;

const LeadsTableViewContainer = Styled.View<{responsive: ResponsiveType}>`
  padding-top: 0px;
  flex: 1;
`;

export const SalesPageContentTabs = {
  MY_LEADS: 'my-leads',
  COMPANY_LEADS: 'company-leads',
};
export type SalesPageContentTabsType =
  (typeof SalesPageContentTabs)[keyof typeof SalesPageContentTabs];

const getStageTabs = ({
  viewer,
  stageProjectCounts,
}: {
  viewer: UserModel;
  stageProjectCounts?: {stageUuid: string; count: number}[];
}) => {
  const orderedStages = Stage.getOrderedStages({
    stages: viewer.viewingOrganization.companySettings.salesStages,
  });
  return orderedStages.map((stage: StageModel) => ({
    key: stage.id,
    label: stage.name,
    value: stage.uuid,
    count: _.find(stageProjectCounts, {stageUuid: stage.uuid})?.count || 0,
    color: stage.color,
  }));
};

const PROJECT_WIDGET_KIND_TO_ICON = {
  [ProjectWidgetKind.PROJECT_DETAILS]: {
    icon: ProjectWidgetKind.getIcon(ProjectWidgetKind.PROJECT_DETAILS),
    tooltip: 'Project Details',
  },
  [ProjectWidgetKind.CONVERSATIONS]: {
    icon: ProjectWidgetKind.getIcon(ProjectWidgetKind.CONVERSATIONS),
    tooltip: 'Conversations',
  },
  [ProjectWidgetKind.COMMENTS]: {
    icon: ProjectWidgetKind.getIcon(ProjectWidgetKind.COMMENTS),
    tooltip: 'Comments',
  },
  [ProjectWidgetKind.EVENT_LOG]: {
    icon: ProjectWidgetKind.getIcon(ProjectWidgetKind.EVENT_LOG),
    tooltip: 'Event Log',
  },
};

const getPaneDefinitions = ({
  loading,
  viewer,
  paginatedList,
  projectCountsByStage,
  pagination,
  urlFilters,
  widget,
  projectUuid,
  pageContentWidth,
  tab,
  projectPaneWidgets,
}: {
  loading: boolean;
  viewer?: UserModel;
  paginatedList?: {
    projects: ProjectModel[];
    paginationMetadata: PaginationType;
  };
  projectCountsByStage?: {
    stageProjectCounts: {stageUuid: string; count: number}[];
  };
  pagination: PaginationType;
  urlFilters: UrlFiltersType<SalesPageContentFilterKeys>;
  widget?: ProjectWidgetKindType;
  projectUuid?: string;
  pageContentWidth: number;
  tab: SalesPageContentTabsType;
  projectPaneWidgets: string[];
}): PaneType[] => {
  const handleCloseProjectPane = () => {
    urlFilters.handleUpdate({
      widget: undefined,
      projectUuid: undefined,
    });
  };

  if (!viewer || !paginatedList || !projectCountsByStage) {
    return [
      {
        key: 'projects-list',
        bodyContentComponent: (
          <SalesPageContentContent
            loading
            pagination={pagination}
            urlFilters={urlFilters}
            tab={tab}
            pageContentWidth={pageContentWidth}
          />
        ),
      },
    ];
  }

  return [
    {
      key: 'projects-list',
      bodyContentComponent: (
        <SalesPageContentContent
          loading={loading}
          viewer={viewer}
          projects={paginatedList.projects}
          pagination={pagination}
          stageProjectCounts={projectCountsByStage.stageProjectCounts}
          urlFilters={urlFilters}
          tab={tab}
          pageContentWidth={pageContentWidth}
        />
      ),
    },
    ...List.insertIf(!!projectUuid, {
      key: 'project-pane',
      width: 400,
      isFullHeight: true,
      isScrollable: true,
      headerContentComponent: (
        <ProjectPaneHeader
          projectUuid={projectUuid!}
          widget={widget}
          handleCloseProjectPane={handleCloseProjectPane}
        />
      ),
      bodyContentComponent: <ProjectPaneContent widget={widget} urlFilters={urlFilters} />,
      railOptions: {
        mobileDrawer: {
          isOpen: !!widget,
          isToolbarVisible: true,
        },
        actions: projectPaneWidgets.map((widgetKind) => ({
          ...PROJECT_WIDGET_KIND_TO_ICON[widgetKind],
          handleAction: () => urlFilters.handleUpdate({widget: widgetKind}),
          isSelected: widget === widgetKind,
        })),
      },
    }),
  ];
};

export type SalesPageContentFilterKeys = {
  page: number;
  salespersonIds: string[];
  stage: string;
  view: string;
  widget: ProjectWidgetKindType;
  projectUuid: string;
} & SalesLeadsFiltersType &
  ProjectDetailsFiltersType &
  ProjectEventLogFiltersType;
export type SalesPageContentUrlFiltersType = UrlFiltersType<SalesPageContentFilterKeys>;
interface LeadsTableViewProps {
  viewer?: UserModel;
  projects?: ProjectModel[];
  pagination: PaginationType;
  stageProjectCounts?: {stageUuid: string; count: number}[];
  loading: boolean;
  pageContentWidth: number;
  urlFilters: SalesPageContentUrlFiltersType;
  handlePressTab?: (value: SalesPageContentTabsType) => void;
}
const LeadsTableView = ({
  viewer,
  projects = [],
  pagination,
  loading,
  pageContentWidth,
  urlFilters,
}: LeadsTableViewProps) => {
  const responsive = useResponsive();

  return (
    <LeadsTableViewContainer responsive={responsive}>
      <SalesLeadsTable
        projects={projects}
        viewer={viewer}
        loading={loading}
        pageContentWidth={pageContentWidth}
        urlFilters={urlFilters}
      />
      <Space height={16} />
      <PaginationBar pagination={pagination} />
      <Space height={responsive.desktop ? 24 : 16} />
    </LeadsTableViewContainer>
  );
};

const SalesPageContentContent = ({
  viewer,
  projects,
  urlFilters,
  pagination,
  stageProjectCounts,
  pageContentWidth,
  tab,
  loading,
}: LeadsTableViewProps & {
  urlFilters: SalesPageContentUrlFiltersType;
  tab: SalesPageContentTabsType;
}) => {
  const responsive = useResponsive();
  const isBoardView = urlFilters.get('view') === 'BOARD';
  const stageTabs = viewer ? getStageTabs({viewer, stageProjectCounts}) : undefined;

  return (
    <SalesPageContentContentContainer responsive={responsive}>
      {/* We actually want a line weight of 4 here for mobile, but since the design
      system already has a line of weight 1, we add a line of weight 3 here. */}
      {!responsive.desktop && <Line weight={3} />}
      <Space height={responsive.desktop ? 24 : 16} />
      <SalesLeadsFilters viewer={viewer} tab={tab} urlFilters={urlFilters} />
      <Space height={16} />
      {!stageTabs ? (
        <LoadingStageTabs />
      ) : (
        <Tabs
          tabs={stageTabs}
          handlePressTab={(tab) => {
            urlFilters.handleUpdate({
              stage: tab.value,
              page: 1,
            });
          }}
          activeTabIndex={stageTabs.findIndex(
            (stageTab) => stageTab.value === urlFilters.get('stage'),
          )}
          scrollViewStyle={{paddingLeft: responsive.desktop ? 24 : 16}}
          TabComponent={({tab, handlePressTab, isActive}) => (
            <Tabs.Tab tab={tab} handlePressTab={handlePressTab} style={{paddingBottom: 6}}>
              <Badge
                label={`${tab.label} (${tab.count})`}
                color={colors.white}
                backgroundColor={tab.color}
                isResponsive
              />
              {isActive && <Tabs.TabUnderline style={{backgroundColor: tab.color}} />}
            </Tabs.Tab>
          )}
        />
      )}
      <LeadsTableView
        viewer={viewer}
        projects={projects}
        pagination={pagination}
        loading={loading}
        urlFilters={urlFilters}
        stageProjectCounts={stageProjectCounts}
        pageContentWidth={pageContentWidth}
      />
    </SalesPageContentContentContainer>
  );
};

const SalesPageContent = ({
  projectPaneWidgets,
  tab,
  handlePressTab,
}: {
  projectPaneWidgets: string[];
  tab: SalesPageContentTabsType;
  handlePressTab: (tab: SalesPageContentTabsType) => void;
}) => {
  const initialFilterValues = {
    page: 1,
  };
  const urlFilters = useUrlFilters<SalesPageContentFilterKeys>({
    getRoute: () => `/sales/${tab}`,
    filterKeys: [
      'page',
      'stage',
      'view',
      'widget',
      'projectUuid',
      // Projects list filters
      'searchQuery',
      ...SALES_LEADS_FILTERS_KEYS,
      // Project event log filters
      'fromDate',
      'toDate',
      'creatorIds',
      'jobIds',
      'isJobsOnly',
    ],
    initialFilterValues,
  });

  const {
    widget: ProjectWidgetKindType,
    projectUuid,
    stage,
    salespersonIds,
    ...queryFilters
  } = urlFilters.getFilters();

  const [stages, setStages] = useState<StageModel[] | undefined>();

  const {networkStatus, loading, data} = useQuery(SalesPageContent.query, {
    fetchPolicy: 'cache-and-network',
    variables: {
      pagination: {
        page: urlFilters.get('page'),
        resultsPerPage: 25,
      },
      sortSettings: [
        {
          field: 'sales_stage_updated_at',
        },
      ],
      salespersonIds: tab === SalesPageContentTabs.COMPANY_LEADS ? salespersonIds : [0],
      stageIds: stages && stage ? [_.find(stages, {uuid: stage})?.id] : [],
      statuses: ProjectStatus.ACTIVE_STATUSES,
      isCancelled: false,
      ...queryFilters,
    },
    // Make sure that the initial filter values are set before querying
    skip: !_.every(initialFilterValues, (_, key) => {
      return urlFilters.get(key as keyof SalesPageContentFilterKeys) !== undefined;
    }),
  });

  useEffect(() => {
    if (data?.viewer && !stages) {
      setStages(data.viewer.viewingOrganization.companySettings.salesStages);
      if (!urlFilters.get('stage')) {
        const stageTabs = getStageTabs({viewer: data.viewer});
        urlFilters.handleUpdate({
          stage: stageTabs[0]?.value,
        });
      }
    }
  }, [data]);

  const pagination = usePagination({
    currentPage: Number(urlFilters.get('page')) || 1,
    paginationMetadata: _.get(data, 'paginatedList.paginationMetadata'),
    onChangePage: (page) => {
      urlFilters.handleUpdate({page});
    },
  });

  const widget = urlFilters.get('widget');
  const stageTabs = data?.viewer ? getStageTabs({viewer: data.viewer}) : [];
  const [pageContentWidth, setPageContentWidth] = useState(0);

  return (
    <PageContentContainer onLayout={(e) => setPageContentWidth(e.nativeEvent.layout.width)}>
      <MultiPaneLayout
        titleText={'Sales'}
        isBeta
        isMobileScrollablePage
        tabOptions={{
          tabs: [
            {
              label: 'My Leads',
              value: SalesPageContentTabs.MY_LEADS,
            },
            {
              label: 'Company Leads',
              value: SalesPageContentTabs.COMPANY_LEADS,
            },
          ],
          activeTabIndex: tab === SalesPageContentTabs.COMPANY_LEADS ? 1 : 0,
          handlePressTab: ({value}: {value: SalesPageContentTabsType}) => {
            urlFilters.handleReset({page: 1, stage: stageTabs[0]?.value});
            handlePressTab(value);
          },
        }}
        paneDefinitions={getPaneDefinitions({
          loading: loading && [1, 2].includes(networkStatus),
          viewer: data?.viewer,
          paginatedList: data?.paginatedList,
          projectCountsByStage: data?.projectCountsByStage,
          pagination,
          urlFilters,
          widget,
          projectUuid,
          tab,
          pageContentWidth,
          projectPaneWidgets,
        })}
      />
    </PageContentContainer>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
SalesPageContent.query = gql`
  ${SalesLeadsFilters.fragment}
  ${SalesLeadsTable.fragment}
  ${SalesLeadsTable.viewerFragment}
  ${Stage.getOrderedStages.fragment}
  ${usePagination.fragment}

  query SalesPageContent(
    $pagination: PaginationInput!
    $searchQuery: String
    $moveStartDate: String
    $moveEndDate: String
    $projectTypeIds: [Int]
    $projectTagIds: [Int]
    $stageIds: [Int]
    $salespersonIds: [Int]
    $coordinatorIds: [Int]
    $crewOrganizationIds: [String]
    $referralSources: [String]
    $sortSettings: [ProjectSortSetting]
    $statuses: [String]
    $isCancelled: Boolean
    ) {
    ${gql.query}
    viewer {
      id
      viewingOrganization {
        id
        companySettings {
          salesStages {
            id
            uuid
            name
            color
            ...Stage_getOrderedStages
          }
        }
      }
      ...SalesLeadsFilters
      ...SalesLeadsTable_viewer
    }
    paginatedList: filteredProjectsPaginatedList(
      pagination: $pagination,
      searchQuery: $searchQuery,
      fromDate: $moveStartDate,
      toDate: $moveEndDate,
      projectTypeIds: $projectTypeIds,
      projectTagIds: $projectTagIds,
      salespersonIds: $salespersonIds,
      coordinatorIds: $coordinatorIds,
      crewOrganizationIds: $crewOrganizationIds,
      referralSources: $referralSources,
      salesStageIds: $stageIds,
      sortSettings: $sortSettings,
      statuses: $statuses,
      isCancelled: $isCancelled,
    ) {
      projects: results {
        id
        salesStageId
        ...SalesLeadsTable
      }
      paginationMetadata {
        currentPage
        hasNext
        ...usePagination
      }
    }
    projectCountsByStage: filteredProjectCountsBySalesStage(
      searchQuery: $searchQuery,
      fromDate: $moveStartDate,
      toDate: $moveEndDate,
      projectTypeIds: $projectTypeIds,
      projectTagIds: $projectTagIds,
      salespersonIds: $salespersonIds,
      coordinatorIds: $coordinatorIds,
      crewOrganizationIds: $crewOrganizationIds,
      referralSources: $referralSources,
    ) {
      stageProjectCounts {
        stageUuid
        count
      }
    }
  }
`;

export default SalesPageContent;
